import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
    useRef,
    useEffect,
} from "react";
import {
    API_BASE_URL,
    fetchSopProcessingStatus,
    uploadDocument,
    type SopProcessingStatusValue,
} from "../services/api";
import { logger } from "../utils/logger";

const POLL_INTERVAL_MS = 5000;

type UploadStatus = "uploading" | "queued" | "processing" | "success" | "error";

interface UploadItem {
    fileId: string;
    fileName: string;
    status: UploadStatus;
    error: string;
    sopTitle: string;
    showIndicator: boolean;
}

interface SubmitSopUploadOptions {
    file: File;
    sopTitle: string;
    onClose: () => void;
    createMetadata: (fileId: string) => Promise<void>;
    onMetadataCreated?: () => void;
    getAdminEmail?: () => Promise<string>;
}

interface UploadContextType {
    uploads: Map<string, UploadItem>;
    startUpload: (fileId: string, fileName: string) => void;
    setProcessingStatus: (fileId: string, sopTitle: string) => void;
    setSuccessStatus: (fileId: string, sopTitle: string) => void;
    setErrorStatus: (fileId: string, error: string) => void;
    closeIndicator: (fileId: string) => void;
    closeSuccessModal: () => void;
    clearUploads: () => void;
    connectSSE: (adminEmail: string) => void;
    disconnectSSE: () => void;
    getUploads: () => UploadItem[];
    showSuccessModal: boolean;
    successSopTitle: string;
    submitSopUpload: (options: SubmitSopUploadOptions) => Promise<string>;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) throw new Error("useUpload must be used within an UploadProvider");
    return context;
};

function mapBackendStatusToUploadStatus(
    backendStatus: SopProcessingStatusValue,
): UploadStatus | null {
    switch (backendStatus) {
        case "pending":
            return "queued";
        case "processing":
            return "processing";
        case "completed":
            return "success";
        case "failed":
        case "deleted":
            return "error";
        default:
            return null;
    }
}

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [uploads, setUploads] = useState<Map<string, UploadItem>>(new Map());
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successSopTitle, setSuccessSopTitle] = useState("");

    const isProcessingRef = useRef(false);
    const currentAdminEmailRef = useRef<string | null>(null);
    const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
    const pollingTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
    const resolvedFileIdsRef = useRef<Set<string>>(new Set());
    const uploadsRef = useRef(uploads);

    useEffect(() => {
        uploadsRef.current = uploads;
    }, [uploads]);

    const stopPolling = useCallback((fileId: string) => {
        const timer = pollingTimersRef.current.get(fileId);
        if (timer) {
            clearInterval(timer);
            pollingTimersRef.current.delete(fileId);
        }
    }, []);

    const stopAllPolling = useCallback(() => {
        pollingTimersRef.current.forEach((timer) => clearInterval(timer));
        pollingTimersRef.current.clear();
    }, []);

    const promoteNextInQueue = useCallback(() => {
        setUploads((prev) => {
            const newUploads = new Map(prev);
            const nextQueued = Array.from(newUploads.values()).find(
                (u) => u.status === "queued",
            );
            if (nextQueued) {
                newUploads.set(nextQueued.fileId, {
                    ...nextQueued,
                    status: "processing",
                });
                isProcessingRef.current = true;
            } else {
                isProcessingRef.current = false;
            }
            return newUploads;
        });
    }, []);

    const setSuccessStatus = useCallback(
        (fileId: string, sopTitle: string) => {
            if (resolvedFileIdsRef.current.has(fileId)) return;
            resolvedFileIdsRef.current.add(fileId);
            stopPolling(fileId);

            setUploads((prev) => {
                const newUploads = new Map(prev);
                const upload = newUploads.get(fileId);
                if (upload) {
                    newUploads.set(fileId, {
                        ...upload,
                        status: "success",
                        sopTitle: sopTitle || upload.sopTitle,
                    });
                }
                return newUploads;
            });
            promoteNextInQueue();

            setTimeout(() => {
                setSuccessSopTitle(sopTitle);
                setShowSuccessModal(true);

                setTimeout(() => {
                    setUploads((prevMap) => {
                        const updated = new Map(prevMap);
                        updated.delete(fileId);
                        return updated;
                    });
                }, 2000);
            }, 1500);
        },
        [promoteNextInQueue, stopPolling],
    );

    const setErrorStatus = useCallback(
        (fileId: string, error: string) => {
            if (resolvedFileIdsRef.current.has(fileId)) return;
            resolvedFileIdsRef.current.add(fileId);
            stopPolling(fileId);

            setUploads((prev) => {
                const newUploads = new Map(prev);
                const upload = newUploads.get(fileId);
                if (upload) {
                    newUploads.set(fileId, { ...upload, status: "error", error });
                }
                return newUploads;
            });
            promoteNextInQueue();
        },
        [promoteNextInQueue, stopPolling],
    );

    const applyPolledStatus = useCallback(
        (
            fileId: string,
            backendStatus: SopProcessingStatusValue,
            processingError?: string | null,
        ) => {
            if (resolvedFileIdsRef.current.has(fileId)) return;

            const mapped = mapBackendStatusToUploadStatus(backendStatus);
            if (!mapped) return;

            const upload = uploadsRef.current.get(fileId);
            const sopTitle = upload?.sopTitle || "";

            if (mapped === "success") {
                setSuccessStatus(fileId, sopTitle);
                return;
            }
            if (mapped === "error") {
                const message =
                    processingError ||
                    (backendStatus === "deleted"
                        ? "SOP was removed during processing"
                        : "Processing failed");
                setErrorStatus(fileId, message);
                return;
            }

            setUploads((prev) => {
                const newUploads = new Map(prev);
                const item = newUploads.get(fileId);
                if (!item || item.status === "success" || item.status === "error") {
                    return prev;
                }
                newUploads.set(fileId, { ...item, status: mapped });
                if (mapped === "processing") {
                    isProcessingRef.current = true;
                }
                return newUploads;
            });
        },
        [setSuccessStatus, setErrorStatus],
    );

    const pollProcessingStatus = useCallback(
        async (fileId: string) => {
            try {
                const result = await fetchSopProcessingStatus(fileId);
                applyPolledStatus(
                    fileId,
                    result.processing_status,
                    result.processing_error,
                );
            } catch (err) {
                logger.error("[Upload] processing-status poll failed", {
                    fileId,
                    error: err,
                });
            }
        },
        [applyPolledStatus],
    );

    const startPolling = useCallback(
        (fileId: string) => {
            if (pollingTimersRef.current.has(fileId)) return;

            void pollProcessingStatus(fileId);
            const timer = setInterval(() => {
                void pollProcessingStatus(fileId);
            }, POLL_INTERVAL_MS);
            pollingTimersRef.current.set(fileId, timer);
        },
        [pollProcessingStatus],
    );

    const beginPendingUpload = useCallback((fileName: string, sopTitle: string) => {
        const pendingId = `pending-${crypto.randomUUID()}`;
        setUploads((prev) => {
            const newUploads = new Map(prev);
            newUploads.set(pendingId, {
                fileId: pendingId,
                fileName,
                status: "uploading",
                error: "",
                sopTitle,
                showIndicator: true,
            });
            return newUploads;
        });
        return pendingId;
    }, []);

    const failPendingUpload = useCallback((pendingId: string, error: string) => {
        setUploads((prev) => {
            const newUploads = new Map(prev);
            const upload = newUploads.get(pendingId);
            if (upload) {
                newUploads.set(pendingId, { ...upload, status: "error", error });
            }
            return newUploads;
        });
    }, []);

    const onUploadAccepted = useCallback(
        (pendingId: string, fileId: string, sopTitle: string) => {
            setUploads((prev) => {
                const newUploads = new Map(prev);
                const pending = newUploads.get(pendingId);
                newUploads.delete(pendingId);

                const status: UploadStatus = isProcessingRef.current
                    ? "queued"
                    : "processing";
                if (status === "processing") {
                    isProcessingRef.current = true;
                }

                newUploads.set(fileId, {
                    fileId,
                    fileName: pending?.fileName || "document.pdf",
                    status,
                    error: "",
                    sopTitle,
                    showIndicator: true,
                });
                return newUploads;
            });
            startPolling(fileId);
        },
        [startPolling],
    );

    const startUpload = useCallback((fileId: string, fileName: string) => {
        setUploads((prev) => {
            const newUploads = new Map(prev);
            newUploads.set(fileId, {
                fileId,
                fileName,
                status: "uploading",
                error: "",
                sopTitle: "",
                showIndicator: true,
            });
            return newUploads;
        });
    }, []);

    const setProcessingStatus = useCallback(
        (fileId: string, sopTitle: string) => {
            setUploads((prev) => {
                const newUploads = new Map(prev);
                const upload = newUploads.get(fileId);
                if (upload) {
                    const cleared = { ...upload, sopTitle, error: "" };
                    if (isProcessingRef.current) {
                        newUploads.set(fileId, { ...cleared, status: "queued" });
                    } else {
                        newUploads.set(fileId, { ...cleared, status: "processing" });
                        isProcessingRef.current = true;
                    }
                }
                return newUploads;
            });
            startPolling(fileId);
        },
        [startPolling],
    );

    const closeIndicator = useCallback((fileId: string) => {
        stopPolling(fileId);
        setUploads((prev) => {
            const newUploads = new Map(prev);
            const upload = newUploads.get(fileId);
            if (upload) {
                newUploads.set(fileId, { ...upload, showIndicator: false });
            }
            return newUploads;
        });
    }, [stopPolling]);

    const closeSuccessModal = useCallback(() => {
        setShowSuccessModal(false);
        setSuccessSopTitle("");
    }, []);

    const disconnectSSE = useCallback(() => {
        if (readerRef.current) readerRef.current.cancel();
        readerRef.current = null;
        currentAdminEmailRef.current = null;
    }, []);

    const clearUploads = useCallback(() => {
        disconnectSSE();
        stopAllPolling();
        resolvedFileIdsRef.current.clear();
        setUploads(new Map());
        setShowSuccessModal(false);
        setSuccessSopTitle("");
        isProcessingRef.current = false;
    }, [disconnectSSE, stopAllPolling]);

    const connectSSE = useCallback(
        (adminEmail: string) => {
            if (currentAdminEmailRef.current === adminEmail && readerRef.current) return;
            disconnectSSE();

            currentAdminEmailRef.current = adminEmail;
            const token = localStorage.getItem("access_token");
            const sseUrl = `${API_BASE_URL}/events/${encodeURIComponent(adminEmail)}`;

            const connectWithAuth = async () => {
                try {
                    const response = await fetch(sseUrl, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "text/event-stream",
                        },
                    });
                    if (!response.ok) throw new Error(`SSE connection failed: ${response.status}`);
                    const reader = response.body?.getReader();
                    if (!reader) throw new Error("Failed to get response body reader");

                    readerRef.current = reader;
                    const decoder = new TextDecoder();

                    const processStream = async () => {
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;
                            const chunk = decoder.decode(value, { stream: true });
                            const lines = chunk.split("\n");

                            for (const line of lines) {
                                if (!line.startsWith("data: ")) continue;
                                try {
                                    const eventData = JSON.parse(line.slice(6));
                                    const { event, data } = eventData;
                                    const fileId = data.file_id;
                                    if (!fileId || resolvedFileIdsRef.current.has(fileId)) {
                                        continue;
                                    }

                                    if (event === "queued") {
                                        setUploads((prev) => {
                                            const newUploads = new Map(prev);
                                            const upload = newUploads.get(fileId);
                                            if (
                                                upload &&
                                                upload.status !== "processing" &&
                                                upload.status !== "success"
                                            ) {
                                                newUploads.set(fileId, {
                                                    ...upload,
                                                    status: "queued",
                                                });
                                            }
                                            return newUploads;
                                        });
                                    } else if (event === "processing") {
                                        setUploads((prev) => {
                                            const newUploads = new Map(prev);
                                            const upload = newUploads.get(fileId);
                                            if (
                                                upload &&
                                                upload.status !== "success" &&
                                                upload.status !== "error"
                                            ) {
                                                newUploads.set(fileId, {
                                                    ...upload,
                                                    status: "processing",
                                                });
                                            }
                                            return newUploads;
                                        });
                                    } else if (event === "completed") {
                                        const upload = uploadsRef.current.get(fileId);
                                        setSuccessStatus(
                                            fileId,
                                            upload?.sopTitle || data.filename || "",
                                        );
                                    } else if (event === "failed") {
                                        setErrorStatus(
                                            fileId,
                                            data.error || "Processing failed",
                                        );
                                    }
                                } catch (err) {
                                    logger.error("Error parsing SSE event:", err);
                                }
                            }
                        }
                    };

                    processStream();
                } catch (err) {
                    logger.error("SSE connection error:", err);
                    setTimeout(() => connectSSE(adminEmail), 3000);
                }
            };

            connectWithAuth();
        },
        [disconnectSSE, setSuccessStatus, setErrorStatus],
    );

    const submitSopUpload = useCallback(
        async (options: SubmitSopUploadOptions): Promise<string> => {
            const fileName = options.file.name || "document.pdf";
            const sopTitle = options.sopTitle.trim();
            const pendingId = beginPendingUpload(fileName, sopTitle);
            options.onClose();

            let fileId: string | undefined;
            try {
                const uploadRes = await uploadDocument(options.file);
                fileId = uploadRes?.file_id || uploadRes?.data?.file_id;
                if (!fileId) {
                    throw new Error("Upload succeeded but no file_id returned");
                }

                logger.log("[SOP Upload] file accepted", { fileId, fileName });
                onUploadAccepted(pendingId, fileId, sopTitle);

                await options.createMetadata(fileId);

                if (options.getAdminEmail) {
                    try {
                        const email = await options.getAdminEmail();
                        currentAdminEmailRef.current = email;
                        connectSSE(email);
                    } catch (err) {
                        logger.error("Failed to connect SSE for upload:", err);
                    }
                }

                options.onMetadataCreated?.();
                return fileId;
            } catch (err: unknown) {
                const message =
                    err instanceof Error ? err.message : "Failed to upload SOP. Please try again.";
                if (fileId) {
                    setErrorStatus(fileId, message);
                } else {
                    failPendingUpload(pendingId, message);
                }
                throw err;
            }
        },
        [
            beginPendingUpload,
            onUploadAccepted,
            connectSSE,
            failPendingUpload,
            setErrorStatus,
        ],
    );

    const getUploads = useCallback(() => Array.from(uploads.values()), [uploads]);

    useEffect(
        () => () => {
            disconnectSSE();
            stopAllPolling();
        },
        [disconnectSSE, stopAllPolling],
    );

    return (
        <UploadContext.Provider
            value={{
                uploads,
                startUpload,
                setProcessingStatus,
                setSuccessStatus,
                setErrorStatus,
                closeIndicator,
                closeSuccessModal,
                clearUploads,
                connectSSE,
                disconnectSSE,
                getUploads,
                showSuccessModal,
                successSopTitle,
                submitSopUpload,
            }}
        >
            {children}
        </UploadContext.Provider>
    );
};
